import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { ScrollArea } from '../../ui/scroll-area';
import { ShieldCheck, X, Loader2, FileText, ExternalLink } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL;

export const DataSecurityModal = ({
  open,
  onOpenChange,
  dataSecurityData,
  loading = false,
  selectedItem,
  onSelectItem,
  onUpdateItem,
  canEdit = false,
}) => {
  const [showDocs, setShowDocs] = useState(false);
  const [docsContent, setDocsContent] = useState('');
  const [docsLoading, setDocsLoading] = useState(false);

  const handleOpenDocs = async () => {
    setDocsLoading(true);
    setShowDocs(true);
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      const res = await axios.get(`${API}/api/platform-settings/docs/technical-architecture`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocsContent(res.data.content || 'No content');
    } catch (e) {
      setDocsContent('Failed to load document');
    } finally {
      setDocsLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'reviewed': return 'bg-green-500';
      case 'approved': return 'bg-blue-500';
      default: return 'bg-amber-500';
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-cyan-600" />
            Data & Security
          </DialogTitle>
          <div className="flex items-center justify-between">
            <DialogDescription>
              System-wide security and feature-specific data requirements {!canEdit && '(Read Only)'}
            </DialogDescription>
            <Button
              variant="outline"
              size="sm"
              className="gap-2 text-xs shrink-0"
              onClick={handleOpenDocs}
            >
              <FileText className="h-3.5 w-3.5" />
              Platform Docs
              <ExternalLink className="h-3 w-3" />
            </Button>
          </div>
        </DialogHeader>
        
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            {/* Metrics Summary */}
            <div className="grid grid-cols-4 gap-4 mb-6">
              <div className="p-4 bg-cyan-50 dark:bg-cyan-900/20 rounded-lg text-center">
                <p className="text-2xl font-bold text-cyan-600">{dataSecurityData.metrics?.review_percent || 0}%</p>
                <p className="text-xs text-cyan-700 dark:text-cyan-300">Review Progress</p>
              </div>
              <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg text-center">
                <p className="text-2xl font-bold text-green-600">{dataSecurityData.metrics?.reviewed || 0}</p>
                <p className="text-xs text-green-700 dark:text-green-300">Reviewed</p>
              </div>
              <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg text-center">
                <p className="text-2xl font-bold text-amber-600">{dataSecurityData.metrics?.pending_review || 0}</p>
                <p className="text-xs text-amber-700 dark:text-amber-300">Pending Review</p>
              </div>
              <div className="p-4 bg-teal-50 dark:bg-teal-900/20 rounded-lg text-center">
                <p className="text-2xl font-bold text-teal-600">{dataSecurityData.metrics?.needs_security_count || 0}</p>
                <p className="text-xs text-teal-700 dark:text-teal-300">Need Security</p>
              </div>
            </div>
            
            {/* System-Wide Security Section */}
            <div className="mb-6 p-4 border border-cyan-200 dark:border-cyan-800 rounded-lg bg-cyan-50/50 dark:bg-cyan-900/10">
              <h3 className="text-sm font-semibold text-cyan-800 dark:text-cyan-300 mb-4 flex items-center gap-2">
                <ShieldCheck className="h-4 w-4" />
                System-Wide Security (RHINO-1 Protection)
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600 dark:text-slate-400">Authentication</span>
                    <Badge variant="outline">{dataSecurityData.system_security?.auth_method || 'JWT'}</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600 dark:text-slate-400">Encryption at Rest</span>
                    <Badge className={dataSecurityData.system_security?.encryption_at_rest ? 'bg-green-500' : 'bg-red-500'}>
                      {dataSecurityData.system_security?.encryption_at_rest ? 'Enabled' : 'Disabled'}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600 dark:text-slate-400">Encryption in Transit</span>
                    <Badge className={dataSecurityData.system_security?.encryption_in_transit ? 'bg-green-500' : 'bg-red-500'}>
                      {dataSecurityData.system_security?.encryption_in_transit ? 'HTTPS' : 'Disabled'}
                    </Badge>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600 dark:text-slate-400">Firewall</span>
                    <Badge className={dataSecurityData.system_security?.firewall_enabled ? 'bg-green-500' : 'bg-amber-500'}>
                      {dataSecurityData.system_security?.firewall_enabled ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600 dark:text-slate-400">DDoS Protection</span>
                    <Badge className={dataSecurityData.system_security?.ddos_protection ? 'bg-green-500' : 'bg-amber-500'}>
                      {dataSecurityData.system_security?.ddos_protection ? 'Active' : 'Not Configured'}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600 dark:text-slate-400">Backup Frequency</span>
                    <Badge variant="outline">{dataSecurityData.system_security?.backup_frequency || 'Daily'}</Badge>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Feature Security Items */}
            <div className="flex gap-4">
              {/* Items List */}
              <div className={`${selectedItem ? 'w-1/2' : 'w-full'}`}>
                <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">
                  Feature Data & Security ({dataSecurityData.items?.length || 0})
                </h3>
                <ScrollArea className="h-[300px]">
                  <div className="space-y-2 pr-2">
                    {dataSecurityData.items?.map((item, idx) => (
                      <div 
                        key={item.id || idx}
                        onClick={() => onSelectItem(item)}
                        className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                          selectedItem?.id === item.id 
                            ? 'border-cyan-500 bg-cyan-50 dark:bg-cyan-900/20' 
                            : 'border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/50'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <h4 className="font-medium text-slate-800 dark:text-slate-200 text-sm truncate">{item.name}</h4>
                          <div className="flex items-center gap-1">
                            {item.needs_security && <ShieldCheck className="h-3 w-3 text-cyan-500" />}
                            <Badge className={`text-[10px] ${getStatusColor(item.status)}`}>
                              {item.status || 'pending'}
                            </Badge>
                          </div>
                        </div>
                        {item.data_storage_type && (
                          <p className="text-xs text-cyan-600 dark:text-cyan-400">Storage: {item.data_storage_type}</p>
                        )}
                      </div>
                    ))}
                    {(!dataSecurityData.items || dataSecurityData.items.length === 0) && (
                      <div className="text-center py-8 text-slate-500">
                        No security items yet. Add features via Scope to populate.
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </div>
              
              {/* Detail Panel */}
              {selectedItem && (
                <div className="w-1/2 border-l border-slate-200 dark:border-slate-700 pl-4">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-semibold text-slate-800 dark:text-slate-200">{selectedItem.name}</h4>
                    <Button variant="ghost" size="sm" onClick={() => onSelectItem(null)}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  
                  <ScrollArea className="h-[280px]">
                    <div className="space-y-4 pr-2">
                      {/* Status & Security Level */}
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-xs font-medium text-slate-600 dark:text-slate-400">Status</label>
                          {canEdit ? (
                            <select
                              value={selectedItem.status || 'pending'}
                              onChange={(e) => onUpdateItem(selectedItem.id, { status: e.target.value })}
                              className="w-full mt-1 p-2 border rounded-md text-sm bg-background"
                            >
                              <option value="pending">Pending Review</option>
                              <option value="reviewed">Reviewed</option>
                              <option value="approved">Approved</option>
                            </select>
                          ) : (
                            <p className="mt-1 p-2 text-sm capitalize bg-slate-50 dark:bg-slate-800 rounded-md">
                              {selectedItem.status || 'Pending'}
                            </p>
                          )}
                        </div>
                        <div>
                          <label className="text-xs font-medium text-slate-600 dark:text-slate-400">Security Level</label>
                          {canEdit ? (
                            <select
                              value={selectedItem.security_level || 'standard'}
                              onChange={(e) => onUpdateItem(selectedItem.id, { security_level: e.target.value })}
                              className="w-full mt-1 p-2 border rounded-md text-sm bg-background"
                            >
                              <option value="standard">Standard</option>
                              <option value="high">High</option>
                              <option value="critical">Critical</option>
                            </select>
                          ) : (
                            <p className="mt-1 p-2 text-sm capitalize bg-slate-50 dark:bg-slate-800 rounded-md">
                              {selectedItem.security_level || 'Standard'}
                            </p>
                          )}
                        </div>
                      </div>
                      
                      {/* Needs Security Toggle */}
                      <div className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-800 rounded-lg">
                        <span className="text-sm text-slate-600 dark:text-slate-400">Needs Security Protection?</span>
                        {canEdit ? (
                          <button
                            onClick={() => onUpdateItem(selectedItem.id, { 
                              needs_security: !selectedItem.needs_security 
                            })}
                            className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                              selectedItem.needs_security 
                                ? 'bg-cyan-500 text-white' 
                                : 'bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-400'
                            }`}
                          >
                            {selectedItem.needs_security ? 'Yes' : 'No'}
                          </button>
                        ) : (
                          <Badge className={selectedItem.needs_security ? 'bg-cyan-500' : 'bg-slate-400'}>
                            {selectedItem.needs_security ? 'Yes' : 'No'}
                          </Badge>
                        )}
                      </div>
                      
                      {/* Data Storage */}
                      <div>
                        <label className="text-xs font-medium text-slate-600 dark:text-slate-400">Data Storage Type</label>
                        {canEdit ? (
                          <select
                            value={selectedItem.data_storage_type || ''}
                            onChange={(e) => onUpdateItem(selectedItem.id, { data_storage_type: e.target.value })}
                            className="w-full mt-1 p-2 border rounded-md text-sm bg-background"
                          >
                            <option value="">Select storage type...</option>
                            <option value="mongodb">MongoDB</option>
                            <option value="postgresql">PostgreSQL</option>
                            <option value="s3">AWS S3</option>
                            <option value="local">Local Storage</option>
                            <option value="redis">Redis Cache</option>
                            <option value="none">No Data Storage</option>
                          </select>
                        ) : (
                          <p className="mt-1 p-2 text-sm bg-slate-50 dark:bg-slate-800 rounded-md">
                            {selectedItem.data_storage_type || 'Not set'}
                          </p>
                        )}
                      </div>
                      
                      {/* Database Name */}
                      <div>
                        <label className="text-xs font-medium text-slate-600 dark:text-slate-400">Database/Collection Name</label>
                        {canEdit ? (
                          <Input
                            placeholder="e.g., users, orders, sessions"
                            value={selectedItem.database_name || ''}
                            onChange={(e) => onUpdateItem(selectedItem.id, { database_name: e.target.value })}
                            className="mt-1"
                          />
                        ) : (
                          <p className="mt-1 p-2 text-sm bg-slate-50 dark:bg-slate-800 rounded-md">
                            {selectedItem.database_name || 'Not set'}
                          </p>
                        )}
                      </div>
                      
                      {/* Storage Location */}
                      <div>
                        <label className="text-xs font-medium text-slate-600 dark:text-slate-400">Storage Location</label>
                        {canEdit ? (
                          <Input
                            placeholder="e.g., AWS us-east-1, on-premise"
                            value={selectedItem.storage_location || ''}
                            onChange={(e) => onUpdateItem(selectedItem.id, { storage_location: e.target.value })}
                            className="mt-1"
                          />
                        ) : (
                          <p className="mt-1 p-2 text-sm bg-slate-50 dark:bg-slate-800 rounded-md">
                            {selectedItem.storage_location || 'Not set'}
                          </p>
                        )}
                      </div>
                      
                      {/* Scale Recommendations */}
                      <div>
                        <label className="text-xs font-medium text-slate-600 dark:text-slate-400">Scale & Protection Recommendations</label>
                        {canEdit ? (
                          <Textarea
                            placeholder="How to protect and scale this data..."
                            value={selectedItem.scale_recommendations || ''}
                            onChange={(e) => onUpdateItem(selectedItem.id, { scale_recommendations: e.target.value })}
                            className="mt-1 min-h-[60px]"
                          />
                        ) : (
                          <p className="mt-1 p-2 text-sm bg-slate-50 dark:bg-slate-800 rounded-md min-h-[60px]">
                            {selectedItem.scale_recommendations || 'No recommendations'}
                          </p>
                        )}
                      </div>
                      
                      {/* Security Considerations */}
                      <div>
                        <label className="text-xs font-medium text-slate-600 dark:text-slate-400">Security Considerations</label>
                        {canEdit ? (
                          <Textarea
                            placeholder="Security measures needed (one per line)"
                            value={(selectedItem.security_considerations || []).join('\n')}
                            onChange={(e) => onUpdateItem(selectedItem.id, { 
                              security_considerations: e.target.value.split('\n').filter(r => r.trim())
                            })}
                            className="mt-1 min-h-[60px]"
                          />
                        ) : (
                          <div className="mt-1 p-2 text-sm bg-slate-50 dark:bg-slate-800 rounded-md min-h-[60px]">
                            {selectedItem.security_considerations?.length > 0 ? (
                              <ul className="list-disc list-inside">
                                {selectedItem.security_considerations.map((item, i) => (
                                  <li key={i}>{item}</li>
                                ))}
                              </ul>
                            ) : (
                              <span className="text-muted-foreground">No security considerations listed</span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </ScrollArea>
                </div>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>

    {/* Platform Docs Viewer */}
    <Dialog open={showDocs} onOpenChange={setShowDocs}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-blue-600" />
            RHINO-1 Technical Architecture & Due Diligence
          </DialogTitle>
          <DialogDescription>
            Full platform documentation for leadership review
          </DialogDescription>
        </DialogHeader>
        {docsLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="prose prose-sm dark:prose-invert max-w-none">
            <pre className="whitespace-pre-wrap text-xs leading-relaxed font-sans bg-transparent p-0 border-0" style={{fontFamily: 'inherit'}}>
              {docsContent.split('\n').map((line, i) => {
                if (line.startsWith('# ')) return <h1 key={i} className="text-xl font-bold mt-6 mb-2 text-primary">{line.replace('# ', '')}</h1>;
                if (line.startsWith('## ')) return <h2 key={i} className="text-lg font-bold mt-5 mb-2 text-primary/90 border-b border-border pb-1">{line.replace('## ', '')}</h2>;
                if (line.startsWith('### ')) return <h3 key={i} className="text-base font-semibold mt-4 mb-1">{line.replace('### ', '')}</h3>;
                if (line.startsWith('---')) return <hr key={i} className="my-4 border-border" />;
                if (line.startsWith('| ')) return <div key={i} className="font-mono text-[11px] leading-tight">{line}</div>;
                if (line.startsWith('- **')) return <div key={i} className="ml-4 my-0.5"><span className="font-semibold">{line.replace('- **', '').split('**')[0]}</span>{line.split('**').slice(2).join('**').replace(/^ — /, ' — ')}</div>;
                if (line.startsWith('- ')) return <div key={i} className="ml-4 my-0.5">• {line.replace('- ', '')}</div>;
                if (line.startsWith('```')) return null;
                if (line.trim() === '') return <div key={i} className="h-2" />;
                return <div key={i} className="my-0.5">{line}</div>;
              })}
            </pre>
          </div>
        )}
      </DialogContent>
    </Dialog>
    </>
  );
};

export default DataSecurityModal;
